import type {
  MubinsoBinsoInfo,
  MubinsoCompareRow,
  MubinsoTemplate,
} from "@/utils/buildMubinsoPageProps";

interface MubinsoAboutProps {
  companyName: string;
  binsoInfo: MubinsoBinsoInfo | null;
  compareRows: MubinsoCompareRow[];
  /** B 는 정의 문단과 빈소 문단을 빼고 비교표만 둔다. 빈소 정보는 B 에서 앞쪽 요금표가 보여준다 */
  variant?: MubinsoTemplate;
}

/**
 * 무빈소가 무엇인지 설명하는 섹션.
 *
 * 비용보다 앞에 둔다. "무빈소 장례" 로 검색해 들어온 사람은 아직 이게 뭔지
 * 모르는데 가격부터 보여주면 판단할 근거가 없다.
 *
 * 일반론만 쓰면 1,023개 페이지가 같은 문단을 갖게 되므로, 이 장례식장의
 * 빈소 현황과 고이 상품 데이터를 엮어 페이지마다 다른 내용이 되게 한다.
 */
export const MubinsoAbout = ({
  companyName,
  binsoInfo,
  compareRows,
  variant = "A",
}: MubinsoAboutProps) => {
  const isB = variant === "B";
  return (
    <section className="mb-about" aria-labelledby="mb-about-title">
      <h2 id="mb-about-title" className="mb-about__title">
        {isB ? "무빈소장과 가족장, 무엇이 다른가요" : "무빈소 장례란?"}
      </h2>

      {!isB && (
        <p className="mb-about__lede">
          빈소를 차리지 않고 진행하는 장례입니다. 조문을 받지 않는 대신, 가족이
          고인과 인사를 나누는 입관식과 발인, 화장 등 핵심 절차는 그대로 갖춰
          진행합니다. 빈소 없는 장례, 조문객 없는 장례라고도 부릅니다.
        </p>
      )}

      {!isB && binsoInfo && (
        <p className="mb-about__parlor">
          {`${companyName}은 빈소 ${binsoInfo.count}실을 운영하고 사용료는 ${binsoInfo.minLabel}부터 ${binsoInfo.maxLabel}까지입니다. 무빈소로 진행하면 이 빈소 사용료와 조문객 음식 비용이 들지 않습니다. 다만 안치실은 법령상 사망 후 24시간 이내에 화장할 수 없어 반드시 사용합니다.`}
        </p>
      )}

      <div className="mb-about__table-wrap">
        <table className="mb-compare">
          <caption className="mb-compare__caption">
            {`${companyName}에서 무빈소장과 가족장 비교`}
          </caption>
          <thead>
            <tr>
              <th scope="col">항목</th>
              <th scope="col" className="mb-compare__accent">
                무빈소장
              </th>
              <th scope="col">가족장</th>
            </tr>
          </thead>
          <tbody>
            {compareRows.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                <td className="mb-compare__accent">{row.mubinso}</td>
                <td>{row.family}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mb-about__note">
        {isB
          ? "* 상조 비용은 고이 상품 가격, 장례식장 비용은 e하늘 기반 추산액입니다."
          : "* 고이 무빈소장, 가족장 상품 기준입니다. 장례식장에 내는 시설 이용료는 별도이며 장례식장마다 다릅니다."}
      </p>
    </section>
  );
};
